import isNil from 'lodash/isNil';

import { Team } from './types/model-app';

export const getTeamChildren = (teamId: string, teams: Team[] | undefined, arrayToMutate: string[]) => {
  if (!isNil(teams)) {
    const children = teams.filter((team) => team.parentId === teamId);
    for (const child of children) {
      arrayToMutate.push(child.id);
      getTeamChildren(child.id, teams, arrayToMutate);
    }
  }
};

export const getTeamParents = (team: Team, teams: Team[] | undefined, arrayToMutate: string[]) => {
  if (!isNil(teams)) {
    const parent = teams.find((t) => t.id === team.parentId);
    if (!isNil(parent)) {
      arrayToMutate.push(parent.id);
      getTeamParents(parent, teams, arrayToMutate);
    }
  }
};

/**
 * Turn an unorganized list of team into a hierarchical one.
 */
export const getTreeTeams = (teams: Team[]) => {
  const hashTable = Object.create(null);
  // eslint-disable-next-line no-return-assign
  teams.forEach((team) => (hashTable[team.id] = { ...team, children: [] }));
  const dataTree: Team[] = [];
  teams.forEach((team) => {
    if (team.parentId && hashTable[team.parentId]) hashTable[team.parentId].children.push(hashTable[team.id]);
    else dataTree.push(hashTable[team.id]);
  });
  return dataTree;
};

/**
 * Given a hierarchical team list, returns the one that is at its bottom
 */
export const getLeafTeam = (teams: Team[]) => {
  return teams.find((team) => !teamHasChildInList(team, teams));
};

const teamHasChildInList = (team: Team, list: Team[]) => {
  return list.some((t) => t.parentId === team.id);
};

export const getCompleteTeams = (teamIds: string[], getTeam: (id: string) => Team | undefined) => {
  return teamIds.reduce((acc: Team[], teamId: string) => {
    const completeTeam = getTeam(teamId);
    if (!isNil(completeTeam)) acc.push(completeTeam);
    return acc;
  }, []);
};

export interface FlattenedTeam {
  team: Team;
  ancestorNames: string[];
}

/**
 * Depth-first list of a hierarchical team list (see `getTreeTeams`), each team with the names of its ancestors.
 */
export const flattenTeamTree = (treeTeams: Team[], ancestorNames: string[] = []): FlattenedTeam[] =>
  treeTeams.flatMap((team) => [
    { team, ancestorNames },
    ...flattenTeamTree(team.children ?? [], [...ancestorNames, team.name]),
  ]);

export interface RelatedTeams {
  ancestors: Team[];
  descendants: Team[];
}

/**
 * Splits `candidateTeams` into the ancestors and the descendants of the team `teamId`; unrelated teams are dropped.
 */
export const getRelatedTeams = (candidateTeams: Team[], teamId: string, teams: Team[]): RelatedTeams =>
  candidateTeams.reduce<RelatedTeams>(
    (acc, candidateTeam) => {
      const descendantIds: string[] = [];
      getTeamChildren(candidateTeam.id, teams, descendantIds);
      if (descendantIds.includes(teamId)) {
        return { ...acc, ancestors: [...acc.ancestors, candidateTeam] };
      }
      const ancestorIds: string[] = [];
      getTeamParents(candidateTeam, teams, ancestorIds);
      if (ancestorIds.includes(teamId)) {
        return { ...acc, descendants: [...acc.descendants, candidateTeam] };
      }
      return acc;
    },
    { ancestors: [], descendants: [] }
  );
